/**
 * An admin's second step — the passkey, MyRave's own QR for the passkey on the PHONE, and the authenticator
 * code — drawn EXACTLY to the user's Stitch design of 2026-10-30 (`plan/design-templates/2fa_login_screen.zip`
 * and `2fa_login_screen_auth.zip`): the account chip, one card with three views, "Cancel & sign out" and Help.
 * Left out of the design: its state switcher, "FIDO2 / WebAuthn Active", the session id, "Zero-Signal Cached",
 * and "Don't ask for 2FA on this device for 30 days" (no such rule — every sign-in asks).
 *
 * - **Passkey** (the default where the account owes one): the prompt opens by itself (the user, 2026-10-29),
 *   the button asks again; "Not on this device? Use your phone" opens the QR view; the code is a link below
 *   when the account may use it instead (`codeInstead`).
 * - **QR** (the user, 2026-11-02: "MyRave's own QR in the card"): `POST /me/mfa/pair` → the QR of the app's
 *   `/approve-sign-in#p=…`, the number to choose on the phone, two minutes ticking; asked every two seconds
 *   (`GET /me/mfa/pair/:id`) until the phone approves — the server then passes this sign-in's passkey step.
 *   It works where this browser cannot hold a passkey at all.
 * - **Code**: six boxes (one real field under them — the phone's one-time-code fill and a paste still work),
 *   "Verify & continue"; a code already used is cleared so the same digits are not sent twice.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import { passkeysAvailable, passkeysNeedNewerApp, usePasskey, type RequestOptionsJSON } from '@/lib/passkey';
import { supportUrl } from '@/lib/tenant';
import { BrandLockup } from '@/ui';
import { useT } from '@/i18n';
import { useAuth } from './AuthProvider';
import '@/admin/fonts/fonts.css';
import './SecondStep.css';

type View = 'passkey' | 'qr' | 'code';

/** How often the computer asks whether the phone has approved. */
export const PAIR_POLL_MS = 2000;

const messageOf = (err: unknown, fallback: string) => (err instanceof ApiError ? err.message : fallback);

export function SecondStep({ start, codeInstead, onPassed }: {
  /** What this sign-in owes: the passkey (the code maybe instead), or the code. */
  start: 'passkey' | 'code';
  codeInstead: boolean;
  onPassed: () => void;
}) {
  const [view, setView] = useState<View>(start === 'code' ? 'code' : 'passkey');
  const toCode = start === 'code' || codeInstead ? () => setView('code') : undefined;
  return (
    <Shell>
      {view === 'passkey' && <PasskeyView onPassed={onPassed} onQr={() => setView('qr')} onCode={toCode} />}
      {view === 'qr' && <QrView onPassed={onPassed} onBack={() => setView('passkey')} onCode={toCode} />}
      {view === 'code' && <CodeView onPassed={onPassed} onBack={start === 'passkey' ? () => setView('passkey') : undefined} />}
    </Shell>
  );
}

/** The page: the brand, the account chip, the card, the foot. */
function Shell({ children }: { children: ReactNode }) {
  const tr = useT();
  const { user, signOut } = useAuth();
  const who = user?.email ?? user?.displayName ?? '';
  const initials = (user?.displayName ?? user?.email ?? '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');
  return (
    <div className="ss-page">
      <header className="ss-top"><BrandLockup size={26} href="/" /></header>
      <main className="ss-main">
        <div className="ss-column">
          {who && (
            <div className="ss-account">
              <span className="ss-avatar" aria-hidden="true">{initials}</span>
              <span className="ss-account-text">
                <span className="ss-account-who">{who}</span>
                {user?.displayName && user.email && <span className="ss-account-name">{user.displayName}</span>}
              </span>
              <button type="button" className="ss-switch" onClick={() => void signOut()}>{tr('Switch account')}</button>
            </div>
          )}
          <section className="ss-card">
            <span className="ss-glow" aria-hidden="true" />
            {children}
          </section>
          <div className="ss-foot">
            <button type="button" className="ss-foot-link" onClick={() => void signOut()}>
              <Ico d="M19 12H5M11 6l-6 6 6 6" />{tr('Cancel & sign out')}
            </button>
            <a className="ss-foot-link" href={supportUrl('/')}>{tr('Help')}</a>
          </div>
        </div>
      </main>
    </div>
  );
}

function PasskeyView({ onPassed, onQr, onCode }: { onPassed: () => void; onQr: () => void; onCode?: () => void }) {
  const tr = useT();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const can = passkeysAvailable();
  async function go(auto = false) {
    setBusy(true);
    setError(undefined);
    try {
      const options = await api.post<RequestOptionsJSON>('/me/mfa/passkey/options', {});
      await api.post('/me/mfa/passkey/verify', { response: await usePasskey(options) });
      onPassed();
    } catch (err) {
      // The automatic prompt dismissed (or refused by a browser that wants a tap first): no error — the button
      // is there. A refusal of the SERVER's is still said.
      if (!(auto && !(err instanceof ApiError))) setError(err instanceof ApiError ? err.message : tr('The passkey was not used. Try again.'));
      setBusy(false);
    }
  }
  // The prompt opens by itself, once per screen (a ref: React's double mount in development keeps it).
  const started = useRef(false);
  useEffect(() => {
    if (started.current || !can) return;
    started.current = true;
    void go(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="ss-view">
      <div className="ss-head">
        <h1 className="ss-title">{tr('Use your passkey')}</h1>
        <p className="ss-lede">{tr('Confirm with the passkey on this device.')}</p>
      </div>
      <div className="ss-sensor-wrap">
        <div className="ss-sensor" data-busy={busy || undefined}>
          <span className="ss-ring" /><span className="ss-ring" /><span className="ss-ring" />
          {/* The design's pad is a picture that also starts the prompt; the button below is the one a screen reader names. */}
          <button type="button" className="ss-pad" onClick={() => void go()} disabled={!can || busy} aria-hidden="true" tabIndex={-1}>
            <IconFingerprint />
            <span className="ss-beacon" aria-hidden="true" />
          </button>
        </div>
        <span className="ss-status" data-tone={error ? 'bad' : undefined} role={error ? 'alert' : undefined}>
          <span className="ss-status-dot" aria-hidden="true" />
          {error ?? (can
            ? (busy ? tr('Waiting for your passkey…') : tr('Ready when you are'))
            : passkeysNeedNewerApp()
              ? tr('This version of the app cannot use your passkey. Update MyRave, or sign in from Safari.')
              : tr('This browser cannot use a passkey. Sign in from a computer or a phone’s browser.'))}
        </span>
      </div>
      <div className="ss-actions">
        {can && (
          <button type="button" className="ss-primary" onClick={() => void go()} disabled={busy}>
            <Ico d="M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 20a6 6 0 0 1 9.5-4.9M17 14v6M14 17h6" />{tr('Use your passkey')}
          </button>
        )}
        <button type="button" className="ss-secondary" onClick={onQr}>
          <IconQr />{tr('Not on this device? Use your phone')}
        </button>
      </div>
      {onCode && (
        <div className="ss-rule">
          <button type="button" className="ss-link" onClick={onCode}>
            <Ico d="M4 7h16M4 12h16M4 17h10" />{tr('Use the authenticator code instead')}
          </button>
        </div>
      )}
    </div>
  );
}

interface Pairing { id: string; url: string; code: number; expiresAt: string }

function QrView({ onPassed, onBack, onCode }: { onPassed: () => void; onBack: () => void; onCode?: () => void }) {
  const tr = useT();
  const [pairing, setPairing] = useState<Pairing | null>(null);
  const [state, setState] = useState<'loading' | 'waiting' | 'expired' | 'error'>('loading');
  const [error, setError] = useState<string>();
  const [now, setNow] = useState(() => Date.now());

  const fresh = useCallback(() => {
    setState('loading');
    setError(undefined);
    api.post<Pairing>('/me/mfa/pair', {})
      .then((p) => { setPairing(p); setState('waiting'); })
      .catch((err) => { setError(messageOf(err, tr('That did not work. Try again.'))); setState('error'); });
  }, [tr]);
  useEffect(() => { fresh(); }, [fresh]);

  // Asked every two seconds until the phone approves or the code runs out.
  useEffect(() => {
    if (!pairing || state !== 'waiting') return undefined;
    let live = true;
    const tick = setInterval(() => {
      setNow(Date.now());
      api.get<{ status: 'waiting' | 'approved' | 'expired' }>(`/me/mfa/pair/${encodeURIComponent(pairing.id)}`)
        .then((s) => {
          if (!live) return;
          if (s.status === 'approved') onPassed();
          else if (s.status === 'expired') setState('expired');
        })
        .catch(() => undefined);
    }, PAIR_POLL_MS);
    return () => { live = false; clearInterval(tick); };
  }, [pairing, state, onPassed]);

  const left = pairing ? Math.max(0, Date.parse(pairing.expiresAt) - now) : 0;
  const clock = `${String(Math.floor(left / 60_000)).padStart(2, '0')}:${String(Math.floor((left % 60_000) / 1000)).padStart(2, '0')}`;
  return (
    <div className="ss-view">
      <div className="ss-head">
        <span className="ss-pill" data-tone="cyan"><span className="ss-pill-dot" aria-hidden="true" />{tr('Your phone’s passkey')}</span>
        <h2 className="ss-title">{tr('Scan with your phone')}</h2>
        <p className="ss-lede">{tr('Use the phone where your MyRave passkey is — signed in to MyRave.')}</p>
      </div>
      <div className="ss-qr-wrap">
        {state === 'waiting' && pairing ? (
          <>
            <div className="ss-qr-box">
              <span className="ss-laser" aria-hidden="true" />
              <QrCode text={pairing.url} label={tr('QR code to approve this sign-in on your phone')} />
            </div>
            <div className="ss-match">
              <span className="ss-match-label">{tr('Choose this number on your phone')}</span>
              <span className="ss-match-number" dir="ltr">{pairing.code}</span>
            </div>
            <span className="ss-timer"><IconSync />{tr('The code works for {time}', { time: clock })}</span>
          </>
        ) : state === 'expired' ? (
          <div className="ss-qr-gone">
            <p>{tr('This code has expired.')}</p>
            <button type="button" className="ss-secondary" onClick={fresh}>{tr('Show a new code')}</button>
          </div>
        ) : state === 'error' ? (
          <p role="alert" className="ss-error">{error}</p>
        ) : (
          <div className="ss-qr-box ss-qr-empty" aria-busy="true" />
        )}
      </div>
      <div className="ss-steps">
        <span className="ss-step"><Ico d="M4 8h3l2-3h6l2 3h3v11H4zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" tone="amber" />{tr('Open your phone’s camera and point it at the code.')}</span>
        <span className="ss-step"><Ico d="M9 11V6a2 2 0 1 1 4 0v5M13 10a2 2 0 1 1 4 0v3a6 6 0 0 1-12 0v-2a2 2 0 1 1 4 0" tone="green" />{tr('Choose the number above, then confirm with your passkey.')}</span>
      </div>
      <div className="ss-actions">
        <button type="button" className="ss-secondary ss-strong" onClick={onBack}>
          <Ico d="M19 12H5M11 6l-6 6 6 6" />{tr('Back to this device’s passkey')}
        </button>
        {onCode && <button type="button" className="ss-link ss-center" onClick={onCode}>{tr('Use the authenticator code instead')}</button>}
      </div>
    </div>
  );
}

function CodeView({ onPassed, onBack }: { onPassed: () => void; onBack?: () => void }) {
  const tr = useT();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const digits = value.replace(/\D/g, '').slice(0, 6);
  async function verify() {
    setBusy(true);
    setError(undefined);
    try {
      await api.post('/me/mfa/verify', { code: digits });
      onPassed();
    } catch (err) {
      setError(messageOf(err, tr('That did not work. Try again.')));
      // A spent code is cleared, so the same digits cannot be sent twice (`mfa_code_used`); a typo is kept.
      if (err instanceof ApiError && err.code === 'mfa_code_used') setValue('');
      setBusy(false);
    }
  }
  return (
    <form className="ss-view" onSubmit={(e) => { e.preventDefault(); void verify(); }}>
      <div className="ss-head">
        <span className="ss-pill" data-tone="pink"><Ico d="M7 11V8a5 5 0 0 1 10 0v3M5 11h14v10H5z" />{tr('Authenticator code')}</span>
        <h2 className="ss-title">{tr('Enter your code')}</h2>
        <p className="ss-lede">{tr('Enter the 6-digit code from your authenticator app — Google Authenticator, Microsoft Authenticator, 1Password or any other.')}</p>
      </div>
      <div className="ss-boxes" onClick={() => field.current?.focus()}>
        <input
          ref={field}
          className="ss-boxes-input"
          value={digits}
          onChange={(e) => setValue(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={7}
          aria-label={tr('The code the app shows')}
          aria-invalid={error ? true : undefined}
          autoFocus
        />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} className="ss-box-wrap">
            {i === 3 && <span className="ss-dash" aria-hidden="true">-</span>}
            <span className="ss-box" aria-hidden="true" data-on={i === digits.length || undefined}>{digits[i] ?? ''}</span>
          </span>
        ))}
      </div>
      {error && <p role="alert" className="ss-error">{error}</p>}
      <div className="ss-actions">
        <button type="submit" className="ss-primary" disabled={busy || digits.length < 6}>
          <Ico d="M9 12l2 2 4-4M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" />{tr('Verify & continue')}
        </button>
        {onBack && (
          <button type="button" className="ss-secondary" onClick={onBack}>
            <IconFingerprint small />{tr('Back to your passkey')}
          </button>
        )}
      </div>
      <div className="ss-rule">
        <a className="ss-link" href={supportUrl('/')}>
          <Ico d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4.9 4.9l4.3 4.3M14.8 14.8l4.3 4.3M14.8 9.2l4.3-4.3M4.9 19.1l4.3-4.3" />
          {tr('Lost your phone? Get help')}
        </a>
      </div>
    </form>
  );
}

/** A QR code drawn as SVG squares — no HTML string, no outside service (`uqr`, loaded when needed). */
export function QrCode({ text, label, className = 'ss-qr' }: { text: string; label: string; className?: string }) {
  const [grid, setGrid] = useState<boolean[][] | null>(null);
  useEffect(() => {
    let live = true;
    void import('uqr').then(({ encode }) => { if (live) setGrid(encode(text, { ecc: 'M' }).data); });
    return () => { live = false; };
  }, [text]);
  if (!grid) return <div className={className} />;
  const quiet = 2;
  const size = grid.length + quiet * 2;
  return (
    <svg className={className} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <rect className="ss-qr-bg" width={size} height={size} />
      {grid.flatMap((row, y) => row.map((on, x) => (on ? <rect key={`${x}-${y}`} className="ss-qr-dot" x={x + quiet} y={y + quiet} width={1} height={1} /> : null)))}
    </svg>
  );
}

const Ico = ({ d, tone }: { d: string; tone?: 'amber' | 'green' }) => (
  <svg className="ss-ico" data-tone={tone} viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
const IconQr = () => <Ico d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2M14 18h2v2M18 18h2v2" />;
const IconSync = () => <Ico d="M20 11a8 8 0 0 0-14.9-3M4 4v4h4M4 13a8 8 0 0 0 14.9 3M20 20v-4h-4" />;
function IconFingerprint({ small }: { small?: boolean }) {
  return (
    <svg className={small ? 'ss-ico' : 'ss-print'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M12 11c0 3.5-.6 6.4-2 9M8.4 7.6A5 5 0 0 1 17 11c0 2.5-.2 4.6-.8 6.5M5.6 17.5c.9-1.9 1.4-4 1.4-6.5a5 5 0 0 1 .7-2.6M4 7.8A9 9 0 0 1 21 11c0 1.5-.1 3-.4 4.3M14 20.5c.4-.9.7-1.9.9-2.9M12 4a7 7 0 0 0-1.4.1" />
    </svg>
  );
}

